<template>
  <div class="pa-4 upload-detail-page" v-if="upload">
    <v-row class="ma-0" style="margin: 0 -8px !important">
      <v-col cols="12" md="10" class="pa-2">
        <v-card outlined>
          <div class="d-flex align-center px-4 py-2 page-header-bar">
            <v-btn icon small @click="callBackRoute" class="mr-2">
              <v-icon small>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="text-subtitle-1 font-weight-bold mr-2"
              >Upload Detail</span
            >
            <span class="caption grey--text mr-2">{{ uploadLabel }}</span>
            <span class="status-pill" :class="statusPillClass(upload.status)">
              {{ statusLabel(upload.status) }}
            </span>
            <v-spacer />
            <v-btn
              outlined
              x-small
              color="primary"
              :loading="exportLoading"
              @click="exportToExcel"
            >
              <v-icon size="14" class="mr-1">mdi-download</v-icon>
              Export
            </v-btn>
          </div>
          <v-divider />
          <v-row dense class="grey lighten-5 ma-0 px-4 py-2">
            <v-col cols="6" sm="3">
              <div class="overline grey--text">Vendor</div>
              <div class="font-weight-bold text-uppercase">{{ upload.vendor_name || "-" }}</div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="overline grey--text">Uploaded By</div>
              <div class="font-weight-bold">{{ upload.uploaded_by || "-" }}</div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="overline grey--text">Uploaded On</div>
              <div class="font-weight-bold">
                {{ upload.uploaded_on ? formatDate(upload.uploaded_on, true) : "-" }}
              </div>
            </v-col>
            <v-col cols="6" sm="3">
              <div class="overline grey--text">Total Draft Jobs</div>
              <div class="font-weight-bold">{{ upload.total_draft_subhirejobs }}</div>
            </v-col>
          </v-row>
          <v-divider />
          <v-card-title class="text-subtitle-2 font-weight-bold py-2 px-4 grey lighten-5">
            Draft Jobs
          </v-card-title>
          <v-divider />
          <v-card-text class="py-2">
            <div
              v-for="(draftJob, index) in upload.draft_jobs"
              :key="draftJob.id"
              class="draft-job-block"
            >
              <div
                class="draft-job-header px-3 py-2"
                @click="toggleDraftJob(index)"
              >
                <div class="d-flex align-center justify-space-between flex-wrap">
                  <div class="d-flex align-center flex-wrap">
                    <v-icon size="18" class="mr-1">
                      {{ isDraftJobOpen(index) ? "mdi-chevron-down" : "mdi-chevron-right" }}
                    </v-icon>
                    <span class="font-weight-bold mono mr-2">{{ draftJobCode(index) }}</span>
                    <span class="type-chip mr-2">{{ draftJob.container_type_name || "-" }}</span>
                    <span class="count-chip mr-2">
                      {{ draftJob.items ? draftJob.items.length : 0 }}
                      {{ draftJob.items && draftJob.items.length === 1 ? "container" : "containers" }}
                    </span>
                  </div>
                  <div class="d-flex align-center flex-shrink-0 mt-1 mt-sm-0">
                    <span class="font-weight-bold mr-3">{{ formatCurrency(draftJob.rate) }}</span>
                    <span class="status-pill" :class="draftJobStatusPillClass(draftJob.status)">
                      {{ draftJobStatusLabel(draftJob.status) }}
                    </span>
                  </div>
                </div>
                <div class="draft-job-subheader mt-2">
                  <div
                    class="route-line text-uppercase"
                    v-if="draftJob.route_title || draftJob.lane_title"
                  >
                    {{ draftJob.route_title || draftJob.lane_title }}
                  </div>
                  <div class="item-status-summary">
                    <span class="item-status-summary__item item-status-summary__item--accepted">
                      {{ itemStatusCount(draftJob, "accepted") }} Accepted
                    </span>
                    <span class="item-status-summary__item item-status-summary__item--pending">
                      {{ itemStatusCount(draftJob, "pending") }} Pending
                    </span>
                    <span class="item-status-summary__item item-status-summary__item--rejected">
                      {{ itemStatusCount(draftJob, "rejected") }} Rejected
                    </span>
                  </div>
                </div>
              </div>

              <table
                v-if="isDraftJobOpen(index) && draftJob.items && draftJob.items.length"
                class="container-table"
              >
                <thead>
                  <tr>
                    <th>Container No.</th>
                    <th>Movement Date</th>
                    <th>VBS Reference</th>
                    <th>Status</th>
                    <th class="text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="item in draftJob.items"
                    :key="item.id"
                    :class="{ 'container-row--warning': item.remarks }"
                  >
                    <td>
                      <span class="container-chip mono">{{ item.container_number }}</span>
                      <button
                        v-if="item.remarks"
                        class="issue-badge mt-1"
                        @click="toggleIssues(item.id)"
                      >
                        <v-icon x-small>mdi-alert-circle-outline</v-icon>
                        {{ remarkLines(item.remarks).length }}
                        {{ remarkLines(item.remarks).length === 1 ? "issue" : "issues" }}
                        <v-icon x-small>{{ isIssuesOpen(item.id) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                      </button>
                      <div v-if="item.remarks && isIssuesOpen(item.id)" class="issue-card-list mt-2">
                        <div
                          v-for="(line, lineIndex) in remarkLines(item.remarks)"
                          :key="lineIndex"
                          class="issue-card"
                        >
                          <v-icon x-small color="#92400e" class="mr-2 mt-1">mdi-alert-circle-outline</v-icon>
                          <span>{{ line }}</span>
                        </div>
                      </div>
                    </td>
                    <td>{{ item.movement_date ? formatDate(item.movement_date) : "-" }}</td>
                    <td>
                      <span class="d-flex align-center">
                        <span v-if="item.vbs_reference_no" class="mono font-weight-bold">{{ item.vbs_reference_no }}</span>
                        <span v-else-if="item.emergency_order_container">No VBS ref</span>
                        <span v-else>-</span>
                        <v-menu
                          v-if="item.emergency_order_container"
                          offset-y
                          :close-on-content-click="false"
                          max-width="420"
                        >
                          <template #activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on">
                              <v-icon color="#2563eb">mdi-information-outline</v-icon>
                            </v-btn>
                          </template>
                          <v-card class="pa-4 gate-event-card">
                            <div class="overline grey--text mb-2">Gate Event</div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Container No.</span>
                              <span class="font-weight-bold">{{ item.gate_container_number || "-" }}</span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Container Type</span>
                              <span class="font-weight-bold">{{ item.gate_container_type || "-" }}</span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Gate Out Terminal</span>
                              <span class="font-weight-bold">{{ item.gate_out_terminal || "-" }}</span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Gate In Terminal</span>
                              <span class="font-weight-bold">{{ item.gate_in_terminal || "-" }}</span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Pickup Departure</span>
                              <span class="font-weight-bold">
                                {{ item.pickup_departure ? formatDate(item.pickup_departure, true) : "-" }}
                              </span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Drop Arrival</span>
                              <span class="font-weight-bold">
                                {{ item.drop_arrival ? formatDate(item.drop_arrival, true) : "-" }}
                              </span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row mb-2">
                              <span class="grey--text">Driver</span>
                              <span class="font-weight-bold">{{ item.gate_driver_name || "-" }}</span>
                            </div>
                            <div class="d-flex justify-space-between gate-event-row">
                              <span class="grey--text">Vehicle</span>
                              <span class="font-weight-bold">{{ item.gate_vehicle_number || "-" }}</span>
                            </div>
                          </v-card>
                        </v-menu>
                      </span>
                    </td>
                    <td>
                      <span class="status-pill" :class="itemStatusPillClass(item.status)">
                        {{ itemStatusLabel(item.status) }}
                      </span>
                    </td>
                    <td class="text-right">
                      <div v-if="item.status === 'pending'" class="d-flex align-center justify-end">
                        <v-btn icon small class="mr-1" @click="openEditItemDialog(item)">
                          <v-icon small color="primary">mdi-pencil-outline</v-icon>
                        </v-btn>
                        <v-btn icon small @click="rejectItem(item)">
                          <v-icon small color="error">mdi-close-circle-outline</v-icon>
                        </v-btn>
                      </div>
                      <span v-else class="grey--text">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
              <div
                v-else-if="isDraftJobOpen(index)"
                class="px-3 py-2 caption grey--text"
              >
                No containers in this draft job.
              </div>
            </div>

            <p v-if="!upload.draft_jobs || !upload.draft_jobs.length" class="caption grey--text text-center">
              No draft jobs found for this upload.
            </p>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="2" class="pa-2">
        <v-card outlined class="activity-log-card">
          <v-card-title class="text-subtitle-2 font-weight-bold py-2 px-4 grey lighten-5">
            Activity Log
          </v-card-title>
          <v-divider />
          <v-card-text class="py-2">
            <div class="activity-timeline">
              <div
                v-for="log in upload.activity_log"
                :key="log.id"
                class="activity-timeline-item"
              >
                <div class="activity-timeline-marker"></div>
                <div class="activity-timeline-content">
                  <div class="d-flex justify-space-between flex-wrap">
                    <span class="caption font-weight-bold">{{ log.updated_by_name || "System" }}</span>
                    <span class="caption grey--text">{{ formatDate(log.created, true) }}</span>
                  </div>
                  <div class="caption mt-1">{{ log.message }}</div>
                </div>
              </div>
            </div>
            <p
              v-if="!upload.activity_log || !upload.activity_log.length"
              class="caption grey--text text-center"
            >
              No activity logs yet.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <VendorSubHireDraftJobItemEditDialog
      :value="editItemDialog.show"
      :item="editItemDialog.item"
      role="vendor"
      @input="onEditItemDialogInput"
      @saved="getUploadDetails"
    />

    <NewConfirmationDialog
      v-model="rejectItemDialog.show"
      icon="mdi-close-circle-outline"
      status="error"
      title="Reject Container"
      :text="rejectItemDialog.text"
      confirm-button="Reject"
      reject-button="Cancel"
      show-remarks
      @confirmed="confirmRejectItem"
    />
  </div>
</template>

<script>
import { formatDate, isAuthenticated, hasRouteAccess } from "@/utils/functions";
import { utils, write } from "xlsx";
import VendorSubHireDraftJobItemEditDialog from "@/components/ModuleBased/dialogForms/VendorSubHireDraftJobItemEditDialog.vue";
import NewConfirmationDialog from "@/components/General/NewConfirmationDialog.vue";

export default {
  components: {
    VendorSubHireDraftJobItemEditDialog,
    NewConfirmationDialog,
  },
  data() {
    return {
      upload: null,
      exportLoading: false,
      openDraftJobIndexes: [0],
      openIssueItemIds: [],
      editItemDialog: {
        show: false,
        item: null,
      },
      rejectItemDialog: {
        show: false,
        item: null,
        text: "Are you sure you want to reject this container?",
      },
    };
  },

  computed: {
    uploadLabel() {
      if (!this.upload) return "";
      const vendor = this.upload.vendor_name
        ? this.upload.vendor_name.trim().split(/\s+/)[0].toUpperCase()
        : null;
      const date = this.upload.uploaded_on
        ? formatDate(this.upload.uploaded_on)
        : null;
      return `UPL-${this.upload.id}${vendor ? `-${vendor}` : ""}${date ? `-${date}` : ""}`;
    },
  },

  methods: {
    formatDate,
    remarkLines(remarks) {
      return remarks ? remarks.split("\n").filter((line) => line) : [];
    },
    isIssuesOpen(itemId) {
      return this.openIssueItemIds.includes(itemId);
    },
    toggleIssues(itemId) {
      const pos = this.openIssueItemIds.indexOf(itemId);
      if (pos === -1) {
        this.openIssueItemIds.push(itemId);
      } else {
        this.openIssueItemIds.splice(pos, 1);
      }
    },
    formatCurrency(value) {
      if (value === null || value === undefined) return "-";
      return `AED ${Number(value).toLocaleString()}`;
    },
    draftJobCode(index) {
      const uploadId = this.upload ? this.upload.id : "";
      return `DRF-${uploadId}-${String(index + 1).padStart(2, "0")}`;
    },
    isDraftJobOpen(index) {
      return this.openDraftJobIndexes.includes(index);
    },
    toggleDraftJob(index) {
      const pos = this.openDraftJobIndexes.indexOf(index);
      if (pos === -1) {
        this.openDraftJobIndexes.push(index);
      } else {
        this.openDraftJobIndexes.splice(pos, 1);
      }
    },
    statusLabel(status) {
      const labels = { pending: "Pending", in_process: "In Process", completed: "Completed" };
      return labels[status] || status;
    },
    statusPillClass(status) {
      return `status-pill--${status}`;
    },
    draftJobStatusLabel(status) {
      const labels = { pending: "Pending", partial: "Partial", full: "Accepted", rejected: "Rejected" };
      return labels[status] || status;
    },
    draftJobStatusPillClass(status) {
      return `status-pill--${status}`;
    },
    itemStatusLabel(status) {
      const labels = { pending: "Pending", accepted: "Accepted", rejected: "Rejected" };
      return labels[status] || status;
    },
    itemStatusPillClass(status) {
      return `status-pill--${status}`;
    },
    itemStatusCount(draftJob, status) {
      if (!draftJob.items) return 0;
      return draftJob.items.filter((item) => item.status === status).length;
    },
    openEditItemDialog(item) {
      this.editItemDialog.item = item;
      this.editItemDialog.show = true;
    },
    onEditItemDialogInput(value) {
      this.editItemDialog.show = value;
    },
    rejectItem(item) {
      this.rejectItemDialog.item = item;
      this.rejectItemDialog.text = `Are you sure you want to reject container ${item.container_number}?`;
      this.rejectItemDialog.show = true;
    },
    confirmRejectItem(payload) {
      const item = this.rejectItemDialog.item;
      if (!item) return;
      this.$bus.$emit("showLoader", true);
      this.$api.vendorSubHireUpload
        .rejectVendorDraftJobItem(item.id, payload && payload.remarks)
        .then(() => {
          this.$bus.$emit("showLoader", false);
          this.$bus.$emit("showToastMessage", {
            message: "Container rejected.",
            color: "success",
          });
          this.getUploadDetails();
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          const message =
            (err && err.data && err.data.detail) ||
            (err && err.message) ||
            "Failed to reject container.";
          this.$bus.$emit("showToastMessage", { message, color: "error" });
        });
    },
    callBackRoute() {
      this.$router.push("/app/transporter/bulk-upload/list");
    },
    getUploadDetails() {
      this.$bus.$emit("showLoader", true);
      this.$api.vendorSubHireUpload
        .getVendorSubHireUploadObject(this.$route.params.id)
        .then((res) => {
          this.upload = res.data;
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },
    exportToExcel() {
      if (!this.upload || !this.upload.draft_jobs) return;
      this.exportLoading = true;
      const rows = [];
      this.upload.draft_jobs.forEach((draftJob, index) => {
        (draftJob.items || []).forEach((item) => {
          rows.push({
            "Draft Job": this.draftJobCode(index),
            "Container Type": draftJob.container_type_name || "-",
            Route: draftJob.route_title || draftJob.lane_title || "-",
            Rate: draftJob.rate,
            "Draft Job Status": this.draftJobStatusLabel(draftJob.status),
            "Container No.": item.container_number,
            "Movement Date": item.movement_date
              ? formatDate(item.movement_date)
              : "-",
            "VBS Reference": item.vbs_reference_no || "-",
            Status: this.itemStatusLabel(item.status),
            "Reason for Failure": item.remarks
              ? item.remarks.split("\n").join("; ")
              : "-",
          });
        });
      });

      const fileName = `UPL-${this.upload.id}_${new Date()
        .toISOString()
        .slice(0, 10)}`;
      const wb = utils.book_new();
      wb.Props = { Title: fileName, CreatedDate: new Date() };
      wb.SheetNames.push("Sheet 1");
      wb.Sheets["Sheet 1"] = utils.json_to_sheet(rows);
      const wbout = write(wb, { bookType: "xlsx", type: "binary" });
      const blob = new Blob([this.s2ab(wbout)], {
        type: "application/octet-stream",
      });
      this.download(blob, fileName);
      this.exportLoading = false;
    },
    s2ab(s) {
      const buf = new ArrayBuffer(s.length);
      const view = new Uint8Array(buf);
      for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff;
      return buf;
    },
    download(blob, fileName) {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${fileName}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    },
  },

  beforeMount() {
    if (!isAuthenticated()) {
      this.$router.push("/");
    } else if (!hasRouteAccess(this.$route)) {
      this.$router.push(`/${localStorage.getItem("user_type").toLowerCase()}`);
    } else {
      this.getUploadDetails();
    }
  },
};
</script>

<style scoped>
.gate-event-card {
  min-width: 340px;
}
.gate-event-row {
  font-size: 13.5px;
}

.page-header-bar {
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}

.draft-job-block {
  border: 1px solid #e0e0e0;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  margin-bottom: 10px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.draft-job-header {
  background-color: #fff7ed;
  flex-wrap: wrap;
  cursor: pointer;
}

.container-table {
  width: 100%;
  border-collapse: collapse;
}
.container-table th {
  text-align: left;
  font-size: 12.5px;
  font-weight: 800;
  color: #424242;
  text-transform: uppercase;
  padding: 6px 12px;
  border-bottom: 1px solid #e0e0e0;
}
.container-table td {
  padding: 6px 12px;
  vertical-align: middle;
  border-bottom: 1px solid #e0e0e0;
}
.container-table tr:last-child td {
  border-bottom: none;
}
.container-table tr.container-row--warning {
  background-color: #fff;
}

.issue-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background-color: #fffbeb;
  color: #b45309;
  border: 1px solid #fde68a;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  line-height: 1.6;
}
.issue-badge:hover {
  background-color: #fef3c7;
}

.issue-card-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.issue-card {
  display: flex;
  align-items: flex-start;
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 5px 8px;
  font-size: 11.5px;
  line-height: 1.4;
  max-width: 320px;
}

.mono {
  font-size: 0.85rem;
}

.container-chip {
  background-color: #eef2ff;
  color: #3730a3;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.9rem;
  font-weight: 600;
}

.vbs-chip {
  background-color: #f1f5f9;
  color: #334155;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.85rem;
}

.date-chip {
  background-color: #f5f5f5;
  color: #424242;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 0.85rem;
  font-weight: 700;
}

.type-chip {
  border: 1px solid #d0d0d0;
  border-radius: 12px;
  padding: 1px 10px;
  font-size: 11px;
  font-weight: 600;
  color: #616161;
  background: #fff;
}

.count-chip {
  font-size: 11px;
  font-weight: 600;
  color: #757575;
}

.route-line {
  font-size: 12.5px;
  color: #616161;
}

.draft-job-subheader {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 20px;
}
.item-status-summary {
  position: absolute;
  left: 50%;
  top: 0;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
}
.item-status-summary__item {
  font-size: 11.5px;
  font-weight: 600;
}
.item-status-summary__item--accepted {
  color: #166534;
}
.item-status-summary__item--pending {
  color: #92400e;
}
.item-status-summary__item--rejected {
  color: #991b1b;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 2px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.status-pill--pending {
  background: #fef3c7;
  color: #92400e;
}
.status-pill--in_process,
.status-pill--partial {
  background: #dbeafe;
  color: #1e40af;
}
.status-pill--completed,
.status-pill--full,
.status-pill--accepted {
  background: #dcfce7;
  color: #166534;
}
.status-pill--rejected {
  background: #fee2e2;
  color: #991b1b;
}

.activity-log-card {
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 240px);
  overflow-y: auto;
}

.activity-timeline {
  position: relative;
  padding-left: 28px;
}
.activity-timeline::before {
  content: "";
  position: absolute;
  left: 5px;
  top: 6px;
  bottom: 6px;
  width: 2px;
  background: #ececec;
}
.activity-timeline-item {
  position: relative;
  padding-bottom: 10px;
}
.activity-timeline-item:last-child {
  padding-bottom: 0;
}
.activity-timeline-marker {
  position: absolute;
  left: -28px;
  top: 4px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #f57c00;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #f57c00;
}
.activity-timeline-content {
  border: 1px solid #ececec;
  border-radius: 6px;
  padding: 8px 12px;
}
</style>
